import type { ExampleQuestionsResponse } from '../types/exampleQuestions';
import { isWidgetDebugConsoleEnabled } from '../utils/widgetDebugConsole';

export type ExampleQuestionsRequest = {
    endpoint: string;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    language?: string;
};

export async function fetchExampleQuestions(request: ExampleQuestionsRequest): Promise<ExampleQuestionsResponse> {
    const endpoint = request.endpoint?.trim();

    if (!endpoint) {
        throw new Error('Example questions endpoint is required.');
    }

    const url = new URL(endpoint);
    if (request.language?.trim()) {
        url.searchParams.set('language', request.language.trim());
    }

    if (isWidgetDebugConsoleEnabled()) {
        console.info('[SKF Widget] Requesting example questions:', url.toString());
    }

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: request.headers,
        signal: request.signal
    });

    if (!response.ok) {
        const errorText = await safeReadText(response);
        throw new Error(errorText || `Failed to load example prompts (${response.status}).`);
    }

    try {
        const payload = (await response.json()) as ExampleQuestionsResponse;
        return {
            categories: Array.isArray(payload.categories) ? payload.categories : []
        };
    } catch (error) {
        throw new Error('Example prompts response could not be parsed.');
    }
}

async function safeReadText(response: Response): Promise<string> {
    try {
        return await response.text();
    } catch {
        return '';
    }
}
